import { PublishAlertDialog } from '@/components/AlertDialog/PresetAlerts/PublishAlertDialog'
import {
  HStack,
  PageContainer,
  PageHeader,
  Spread,
  Surface,
  Text,
  VStack,
} from '@/components/Atoms'
import { Card, CardContent } from '@/components/Card'
import { ExportCsvButton } from '@/components/ExportCsv/ExportCsvButton'
import { FocusDot } from '@/components/FocusDot'
import PermissionsGuard from '@/components/Guard/PermissionsGuard'
import { IntroductionDialog } from '@/components/IntroductionDialog'
import { PublishStatusBanner } from '@/components/PublishStatusBanner'
import { Spinner } from '@/components/Spinner'
import { useUploadCsv } from '@/hooks/queries/uploads/useUploadCsv'
import {
  ASSORTMENT_BACKUPS_KEY,
  ASSORTMENT_KEY,
  assortmentExportQueryOptions,
  assortmentSearchSchema,
  useAddAssortmentFromNewMaterials,
  useAssortment,
  usePublishAssortment,
  useUpdateAssortment,
} from '@/hooks/queries/useAssortment'
import { useLastUpdatedAssortmentLog } from '@/hooks/queries/useAuditTrails'
import { usePublishStatus } from '@/hooks/queries/useBcpPublishStatus'
import { formatLastUpdatedAssortmentLog } from '@/lib/utils/assortment'
import { queryClient } from '@/main'
import { INTRO_DIALOGS } from '@/stores/tutorials'
import { AssortmentUpdateBody, ReplaceMaterialsResults } from '@repo/backend/assortment'
import { LAST_UPDATED_ASSORTMENT_LOG } from '@repo/constants'
import { Button } from '@repo/ui/components'
import { toastClassName } from '@repo/ui/lib/constants'
import { createFileRoute } from '@tanstack/react-router'
import { SaveIcon, UploadCloudIcon } from 'lucide-react'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import AssortmentDataTable, { AssortmentTableRow } from './-components/AssortmentDataTable'
import { Backups } from './-components/Backups'
import { ConfigurationMenu } from './-components/ConfigurationMenu'
import {
  KEEP_OLD_FLAGS,
  MaterialsRemplacementMode,
} from './-components/ConfigurationMenu/FlagReplaceModeToggle'
import { UploadAssortmentDataDialog } from './-components/UploadAssortmentDataDialog'

export const Route = createFileRoute('/_auth/_modules/_app/assortment/banners/')({
  component: Assortment,
  validateSearch: (search) => assortmentSearchSchema.parse(search),
})

function Assortment() {
  const { t } = useTranslation(['assortment', 'common'])
  const search = Route.useSearch()

  const { data: assortment } = useAssortment(search)
  const { data: lastUpdatedAssortmentTrail } = useLastUpdatedAssortmentLog()
  const { isPending: isAddAssortmentPending, mutate: addAssortment } =
    useAddAssortmentFromNewMaterials()
  const { mutate: publishAssortment, isPending: isPublishAssortmentPending } =
    usePublishAssortment()
  const { isPublishing: isPublishActive } = usePublishStatus('assortment')

  const [isPublishOpen, setIsPublishOpen] = useState(false)
  const [pendingChanges, setPendingChanges] = useState<AssortmentTableRow[]>([])
  const [materialsReplacementMode, setMaterialsReplacementMode] =
    useState<MaterialsRemplacementMode>(KEEP_OLD_FLAGS)
  const [applyToAllLaunches, setApplyToAllLaunches] = useState<boolean>(false)

  const mutateReplaceMaterialsInAssortment = useUploadCsv<ReplaceMaterialsResults>(
    'replace-materials-assortment',
    [ASSORTMENT_KEY, ASSORTMENT_BACKUPS_KEY, LAST_UPDATED_ASSORTMENT_LOG],
    {
      keepOldFlags: materialsReplacementMode === KEEP_OLD_FLAGS,
    },
  )

  const { isPending: isUpdateAssortmentPending, mutate: updateAssortment } = useUpdateAssortment({
    changesCount: pendingChanges.length,
    queries: {
      ...search,
      page: search.page ?? 1,
    },
    handleOnSuccess: () => setPendingChanges([]),
  })

  const lastUpdatedLog = useMemo(
    () => formatLastUpdatedAssortmentLog(lastUpdatedAssortmentTrail),
    [lastUpdatedAssortmentTrail],
  )

  const updatePendingChanges = (change: AssortmentTableRow) => {
    const isChangeAlreadyPending = pendingChanges.some((c) => c.id === change.id)
    if (isChangeAlreadyPending) {
      setPendingChanges(pendingChanges.map((c) => (c.id === change.id ? change : c)))
      return
    }

    setPendingChanges([...pendingChanges, change])
  }

  const resetPendingChanges = () => {
    setPendingChanges([])

    toast.info('Changes dropped', {
      description: 'Your changes have been reset',
      className: toastClassName,
    })
  }

  const handleSaveChanges = () => {
    if (isUpdateAssortmentPending) return

    updateAssortment(pendingChanges as AssortmentUpdateBody)
  }

  const getCsvExport = () => queryClient.ensureQueryData(assortmentExportQueryOptions(search))

  return (
    <>
      <IntroductionDialog
        dialogId={INTRO_DIALOGS.ASSORTMENTS}
        title={t('tutorials.assortment.dialog.title')}
        guideUrl="/products/assortment-banners/"
      >
        {t('tutorials.assortment.dialog.description')}
      </IntroductionDialog>

      <PageContainer className="pb-28">
        <PublishStatusBanner publishType="assortment" />
        <Spread>
          <PageHeader>{t('assortmentPage.header')}</PageHeader>

          <HStack gap="sm">
            <ConfigurationMenu
              disabled={isPublishActive}
              isAddAssortmentPending={isAddAssortmentPending}
              addAssortment={addAssortment}
              mutateReplaceMaterialsInAssortment={mutateReplaceMaterialsInAssortment}
              materialsReplacementMode={materialsReplacementMode}
              setMaterialsReplacementMode={setMaterialsReplacementMode}
              applyToAllLaunches={applyToAllLaunches}
              setApplyToAllLaunches={setApplyToAllLaunches}
            />

            <UploadAssortmentDataDialog disabled={isPublishActive} />

            <ExportCsvButton getCsvExport={getCsvExport} rowCount={+(assortment?.rowCount ?? 0)} />

            <PermissionsGuard required={['assortment_banners_publish']}>
              <Button onClick={() => setIsPublishOpen(true)} disabled={isPublishActive}>
                <UploadCloudIcon /> {t('actions.publish', { ns: 'common' })}
              </Button>
            </PermissionsGuard>
            <PublishAlertDialog
              title={t('assortmentPage.publishDialog.title')}
              warning={t('assortmentPage.publishDialog.warning')}
              editBlockWarning={t('publish.editBlockWarning', { ns: 'common' })}
              description={
                <p className="text-pretty">
                  {t('assortmentPage.publishDialog.description')}&nbsp;
                  {lastUpdatedLog && (
                    <span className="text-pretty">
                      {t('assortmentPage.publishDialog.dataLastUpdated', {
                        name: lastUpdatedLog.userFullName,
                        date: lastUpdatedLog.formattedDate,
                      })}
                    </span>
                  )}
                </p>
              }
              open={isPublishOpen}
              onOpenChange={setIsPublishOpen}
              onPublish={publishAssortment}
              isPending={isPublishAssortmentPending}
              guideUrl="/products/publishing-changes/publish-assortment"
            />
          </HStack>
        </Spread>

        <Card>
          <CardContent className="pt-6">
            <AssortmentDataTable
              isAddAssortmentPending={isAddAssortmentPending}
              pendingChanges={pendingChanges}
              updatePendingChanges={updatePendingChanges}
              resetPendingChanges={resetPendingChanges}
              isPublishing={isPublishActive}
            />
          </CardContent>
        </Card>
      </PageContainer>

      <Surface padding="md" className="absolute bottom-0 left-0 right-0 border-0 border-t">
        <Spread>
          <VStack>
            <Text>{t('assortmentPage.pendingChanges', { count: pendingChanges.length })}</Text>
            {lastUpdatedLog && (
              <Text tone="muted">
                {t('assortmentPage.lastUpdatedBy', {
                  name: lastUpdatedLog.userFullName,
                  date: lastUpdatedLog.formattedDate,
                })}
              </Text>
            )}
          </VStack>

          <HStack gap="sm">
            <PermissionsGuard
              requiredOneOf={[
                'assortment_banners_edit',
                'assortment_backups_create',
                'assortment_backups_restore',
              ]}
            >
              <Backups />
            </PermissionsGuard>
            <Button
              className="relative min-w-[172px]"
              disabled={pendingChanges.length === 0 || isPublishActive}
              onClick={handleSaveChanges}
            >
              {isUpdateAssortmentPending ? (
                <>
                  <Spinner color="inverted" size="xs" /> {t('assortmentPage.savingChangesButton')}
                </>
              ) : (
                <>
                  {pendingChanges.length > 0 && <FocusDot />}
                  <SaveIcon className="size-4" /> {t('assortmentPage.saveChangesButton')}
                </>
              )}
            </Button>
          </HStack>
        </Spread>
      </Surface>
    </>
  )
}
